/** @jsx jsx */

import { GoogleChartWrapperChartType } from 'react-google-charts';
import { jsx, css } from '@emotion/core';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { escapeHTML } from 'app/developer-v3/pages/insights/utils';
import { BaseChart } from './BaseChart';

const styleChartContainer = css`
  padding-top: 10px;
`;

const html = String.raw;

type ToolTipHtmlString = string;
type ChartColumns = [string, string, object];

interface CommonTimeSeriesChartProps {
  chartType: GoogleChartWrapperChartType;
  data: { dateString: string; value: number }[];
  hAxisFormat: string;
  colors?: string[];
  setHasRenderError: Dispatch<SetStateAction<boolean>>;
  xAxisLabel: string;
  yAxisLabel: string;
}

export function CommonTimeSeriesChart({
  chartType,
  data,
  hAxisFormat,
  colors,
  setHasRenderError,
  xAxisLabel,
  yAxisLabel,
}: CommonTimeSeriesChartProps) {
  const vAxisFormat = data.some(({ value }) => value > 999) ? 'short' : '#';

  const chartColumns: ChartColumns = useMemo(
    () => [
      xAxisLabel, // x-axis
      yAxisLabel, // y-axis
      {
        // custom tooltip stylings
        // https://developers.google.com/chart/interactive/docs/customizing_tooltip_content
        role: 'tooltip',
        type: 'string',
        p: {
          html: true,
        },
      },
    ],
    [xAxisLabel, yAxisLabel]
  );

  // HTML for custom tooltip
  const toolTipHtml = useCallback(
    (date: Date, value: number): ToolTipHtmlString => {
      let dateString = '';

      if (chartType === 'ColumnChart') {
        dateString = format(date, 'MMMM Y');
      } else if (chartType === 'LineChart') {
        // TODO: this should use like `Intl` for formatting or something else...
        // seems unreliable to rely on the locale date string for a user...
        dateString = date.toLocaleDateString().replace(/\//g, '-');
      }

      return html`
        <div>
          <p>${escapeHTML(xAxisLabel)}</p>
          <p>${dateString}</p>
          <br />
          <p>${escapeHTML(yAxisLabel)}</p>
          <p>${value}</p>
        </div>
      `;
    },
    [xAxisLabel, yAxisLabel, chartType]
  );

  const chartData = useMemo(() => {
    const dataPoints = (data || [])
      .flatMap(({ dateString, value }) => {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
          console.error('MonthlyUsersTile: Invalid date:', dateString);
          return [];
        }
        if (typeof value !== 'number') {
          console.error('MonthlyUsersTile: Invalid number:', dateString);
          return [];
        }
        return [[date, value, toolTipHtml(new Date(date), value)]] as [
          Date,
          number,
          string
        ][];
      })
      // sort data points by date, ascending
      .sort(([dateA], [dateB]) => dateB.getTime() - dateA.getTime());
    return [chartColumns, ...dataPoints] as const;
  }, [data, chartColumns, toolTipHtml]);

  return (
    <BaseChart
      chartData={chartData}
      chartType={chartType}
      colors={colors}
      containerStyle={styleChartContainer}
      optionOverrides={{
        vAxis: { format: vAxisFormat },
        hAxis: { format: hAxisFormat },
      }}
      setHasRenderError={setHasRenderError}
    />
  );
}
